import { getNotificationServiceForSharepoint, getShareServiceForSharepoint } from '@fluidx/host-ux';
import type {
    HostScenarioLifecycle,
    HostViewProperties,
    NotificationService,
    ShareService,
} from '@fluidx/office-fluid-types';
import { Locale, resolveLocale } from '@fluidx/utilities';
import { OfficeWebHostScope } from '@ms/office-web-host';
import { getGuid } from 'owa-guid';
import { FluidOwaSource } from 'owa-fluid-validations';
import { getCurrentLanguage } from 'owa-localize';
import { AppHostContext } from './hostContext/AppHostContext';
import { AppNotificationService } from './hostContext/AppNotificationService';
import { AppShareService } from './hostContext/AppShareService';
import { OUTLOOK_APP_NAME } from '../constants';
import { FluidScopeProps } from '../../store/schema/FluidRenderProps';
import { OWAHostViewProperties } from './OWAHostViewProperties';

export function getNotificationServicePromise(
    tokenProvider,
    fileId,
    driveId,
    siteUrl,
    fluidLogger,
    sessionId,
    wellModifierFunction?
): Promise<NotificationService> {
    if (wellModifierFunction) {
        return new Promise(resolve => {
            resolve(new AppNotificationService(wellModifierFunction));
        });
    }
    return getNotificationServiceForSharepoint({
        tokenProvider,
        fileId: fileId,
        driveId: driveId,
        siteUrl: siteUrl,
        logger: fluidLogger,
        correlationId: sessionId,
    });
}

export function getShareServicePromise(
    tokenProvider,
    fluidLogger,
    fileId,
    driveId,
    siteUrl,
    wellModifierFunction?
): Promise<ShareService> {
    if (wellModifierFunction) {
        return new Promise(resolve => {
            resolve(new AppShareService());
        });
    }

    const locale = getCurrentLanguage();
    const fluidLocale: Locale = resolveLocale(locale);

    return getShareServiceForSharepoint({
        tokenProvider,
        logger: fluidLogger,
        appName: OUTLOOK_APP_NAME,
        bootDocumentInfo: {
            fileId: fileId,
            driveId: driveId,
            siteUrl: siteUrl,
        },
        localization: {
            locale: fluidLocale,
        },
    });
}

export function getHostContext(
    sessionId: string,
    scenarioLifecycle: HostScenarioLifecycle,
    scenarioName: string,
    scenarioEntryPoint: string,
    scenarioStartTime: number
) {
    const appHostContext = new AppHostContext(
        sessionId,
        scenarioLifecycle,
        scenarioName,
        scenarioEntryPoint,
        scenarioStartTime,
        getGuid()
    );
    return appHostContext;
}

export function getHostViewProperties(source: FluidOwaSource): HostViewProperties {
    const hostViewProperties = new OWAHostViewProperties(source);
    return hostViewProperties;
}

// gets scope properties that are shared accross all scenarios.
// Does not set ShareServicePromise or NotificationServicePromise as those are different
// based on scenario.
export function getSharedScope(scopeProps: FluidScopeProps): OfficeWebHostScope {
    const {
        commandingSurfaceParts,
        hostContext,
        hostViewProperties,
        presenceColorProvider,
        settingsProvider,
        stylingProvider,
        tokenProvider,
    } = scopeProps;
    return {
        StylingProvider: stylingProvider,
        SettingsProvider: settingsProvider,
        PresenceColorProvider: presenceColorProvider,
        HostContext: hostContext,
        TokenProvider: tokenProvider,
        CommandingSurfaceViewProvider: commandingSurfaceParts.viewProvider,
        HostViewProperties: hostViewProperties,
    };
}
